import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../services/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Silent SSO background initialization
  const initializeAuth = useCallback(async () => {
    try {
      // 1. Check for silent SSO handoff ticket in URL
      const urlParams = new URLSearchParams(window.location.search);
      const ticket = urlParams.get("ticket") || urlParams.get("sso");

      if (ticket) {
        // Clean URL immediately so token/ticket is never exposed to user
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Background silent token exchange
        const res = await api.post("/auth/sso/exchange", { ticket });
        if (res.data?.authenticated && res.data?.user) {
          if (res.data.token) {
            localStorage.setItem("loja_token", res.data.token);
          }
          setUser(res.data.user);
          setLoading(false);
          return;
        }
      }

      // 2. Check existing session via cookie or local token
      const checkRes = await api.get("/auth/sso/silent-check");
      if (checkRes.data?.authenticated && checkRes.data?.user) {
        setUser(checkRes.data.user);
        setLoading(false);
        return;
      }

      const token = localStorage.getItem("loja_token");
      if (token) {
        const meRes = await api.get("/auth/me");
        if (meRes.data?.authenticated && meRes.data?.user) {
          setUser(meRes.data.user);
        } else {
          localStorage.removeItem("loja_token");
          setUser(null);
        }
      }
    } catch (err) {
      // Silent catch - no technical error shown to visitor
      localStorage.removeItem("loja_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const login = async (email, password) => {
    try {
      const res = await api.post("/auth/login", { email, password });
      if (res.data.token) {
        localStorage.setItem("loja_token", res.data.token);
        setUser(res.data.user);
        return true;
      }
    } catch (err) {
      toast.error("E-mail ou senha incorretos. Tente novamente.");
      return false;
    }
  };

  const exchangeSSOTicket = async (ticket) => {
    try {
      const res = await api.post("/auth/sso/exchange", { ticket });
      if (res.data?.authenticated && res.data?.user) {
        if (res.data.token) {
          localStorage.setItem("loja_token", res.data.token);
        }
        setUser(res.data.user);
        return true;
      }
    } catch (err) {
      toast.error("Não foi possível validar sua sessão. Tente novamente.");
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // silent
    } finally {
      localStorage.removeItem("loja_token");
      setUser(null);
      toast.info("Você saiu da conta.");
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, exchangeSSOTicket, refreshAuth: initializeAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
